import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, User, X, Calendar as CalendarIcon, Settings, Ban, Plus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const AdminCalendar = () => {
  const { token } = useAuth();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [blockedTimes, setBlockedTimes] = useState([]);
  const [workingHours, setWorkingHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Dialog states
  const [appointmentDialog, setAppointmentDialog] = useState({ open: false, appointment: null });
  const [blockDialog, setBlockDialog] = useState({ open: false });
  const [settingsDialog, setSettingsDialog] = useState({ open: false });
  const [rescheduleDialog, setRescheduleDialog] = useState({ open: false, appointment: null });
  const [bookDialog, setBookDialog] = useState({ open: false });

  useEffect(() => {
    fetchCalendarData();
    fetchServicesAndClients();
  }, [currentMonth]);

  const fetchServicesAndClients = async () => {
    try {
      const [servicesRes, clientsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/admin/clients`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setServices(servicesRes.data.filter(s => s.active !== false));
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Failed to fetch services/clients:', error);
    }
  };

  const fetchCalendarData = async () => {
    try {
      const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      
      const [appointmentsRes, blockedRes, hoursRes] = await Promise.all([
        axios.get(`${API}/admin/calendar/appointments?start_date=${startDate}&end_date=${endDate}T23:59:59`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/calendar/blocked-times`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/calendar/working-hours`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setAppointments(appointmentsRes.data);
      setBlockedTimes(blockedRes.data);
      setWorkingHours(hoursRes.data.hours);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return appointments.filter(apt => apt.start_time.startsWith(dateStr));
  };

  const isDateBlocked = (date) => {
    const dateStart = new Date(date);
    dateStart.setHours(0, 0, 0, 0);
    const dateEnd = new Date(date);
    dateEnd.setHours(23, 59, 59, 999);
    
    return blockedTimes.some(block => {
      const blockStart = new Date(block.start_datetime);
      const blockEnd = new Date(block.end_datetime);
      return dateStart < blockEnd && dateEnd > blockStart;
    });
  };

  const isWorkingDay = (date) => {
    const dayOfWeek = (date.getDay() + 6) % 7; // Convert to Mon=0
    const hours = workingHours.find(h => h.day_of_week === dayOfWeek);
    return hours?.is_available ?? dayOfWeek < 5;
  };

  const handleBlockTime = async (data) => {
    try {
      await axios.post(`${API}/admin/calendar/blocked-times`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Time blocked successfully');
      setBlockDialog({ open: false });
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to block time');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    try {
      await axios.delete(`${API}/admin/calendar/blocked-times/${blockId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Block removed');
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to remove block');
    }
  };

  const handleSaveWorkingHours = async (hours) => {
    try {
      await axios.put(`${API}/admin/calendar/working-hours`, { hours }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Working hours updated');
      setSettingsDialog({ open: false });
      fetchCalendarData();
    } catch (error) {
      toast.error('Failed to update working hours');
    }
  };

  const handleReschedule = async (appointmentId, newDate, newTime) => {
    try {
      await axios.put(`${API}/admin/appointments/${appointmentId}/reschedule`, {
        new_date: newDate,
        new_time: newTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment rescheduled');
      setRescheduleDialog({ open: false, appointment: null });
      setAppointmentDialog({ open: false, appointment: null });
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reschedule');
    }
  };

  const handleBookAppointment = async (bookingData) => {
    try {
      await axios.post(`${API}/admin/appointments`, bookingData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Appointment booked successfully');
      setBookDialog({ open: false });
      fetchCalendarData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book appointment');
    }
  };

  // Calendar rendering
  const renderHeader = () => (
    <div className="flex items-center justify-between mb-6">
      <div className="flex items-center gap-4">
        <h2 className="font-heading text-2xl">{format(currentMonth, 'MMMM yyyy')}</h2>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(new Date())}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={() => setBookDialog({ open: true })} data-testid="book-appointment-btn" className="bg-primary">
          <Plus className="h-4 w-4 mr-2" />
          Book Appointment
        </Button>
        <Button variant="outline" onClick={() => setBlockDialog({ open: true })} data-testid="block-time-btn">
          <Ban className="h-4 w-4 mr-2" />
          Block Time
        </Button>
        <Button variant="outline" onClick={() => setSettingsDialog({ open: true })} data-testid="working-hours-btn">
          <Settings className="h-4 w-4 mr-2" />
          Working Hours
        </Button>
      </div>
    </div>
  );

  const renderDays = () => (
    <div className="grid grid-cols-7 mb-2">
      {DAYS.map(day => (
        <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
          {day}
        </div>
      ))}
    </div>
  );

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dayAppointments = getAppointmentsForDate(day);
        const isBlocked = isDateBlocked(day);
        const isWorking = isWorkingDay(day);
        const isToday = isSameDay(day, new Date());
        const isCurrentMonth = isSameMonth(day, monthStart);
        const currentDay = day;

        days.push(
          <div
            key={day.toString()}
            onClick={() => setSelectedDate(currentDay)}
            className={`min-h-[100px] p-2 border border-border cursor-pointer transition-colors ${
              !isCurrentMonth ? 'bg-muted/30 text-muted-foreground' :
              isBlocked ? 'bg-red-50' :
              !isWorking ? 'bg-muted/50' :
              'bg-card hover:bg-secondary/30'
            } ${isToday ? 'ring-2 ring-primary ring-inset' : ''}`}
            data-testid={`calendar-day-${format(currentDay, 'yyyy-MM-dd')}`}
          >
            <div className="flex justify-between items-start">
              <span className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>
                {format(day, 'd')}
              </span>
              {isBlocked && <Ban className="h-3 w-3 text-red-500" />}
            </div>
            <div className="mt-1 space-y-1">
              {dayAppointments.slice(0, 3).map(apt => (
                <div
                  key={apt.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAppointmentDialog({ open: true, appointment: apt });
                  }}
                  className={`text-xs p-1 rounded truncate cursor-pointer ${
                    apt.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    apt.status === 'pending_payment' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-blue-100 text-blue-700'
                  }`}
                >
                  {format(parseISO(apt.start_time), 'HH:mm')} {apt.client_name.split(' ')[0]}
                </div>
              ))}
              {dayAppointments.length > 3 && (
                <div className="text-xs text-muted-foreground">
                  +{dayAppointments.length - 3} more
                </div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }

    return <div>{rows}</div>;
  };

  if (loading) {
    return <div className="animate-pulse text-muted-foreground text-center py-8">Loading calendar...</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-calendar">
      {renderHeader()}
      {renderDays()}
      {renderCells()}

      {/* Selected Date Panel */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl p-6 shadow-sm"
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-heading text-xl">{format(selectedDate, 'EEEE, d MMMM yyyy')}</h3>
            <Button variant="ghost" size="sm" onClick={() => setSelectedDate(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {!isWorkingDay(selectedDate) && (
            <p className="text-muted-foreground mb-4">This is a non-working day</p>
          )}
          
          {isDateBlocked(selectedDate) && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">
              <p className="font-medium">Time blocked on this day</p>
              {blockedTimes
                .filter(b => {
                  const blockStart = new Date(b.start_datetime);
                  return format(blockStart, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
                })
                .map(block => (
                  <div key={block.id} className="flex justify-between items-center mt-2">
                    <span className="text-sm">
                      {format(parseISO(block.start_datetime), 'HH:mm')} - {format(parseISO(block.end_datetime), 'HH:mm')}
                      {block.reason && ` (${block.reason})`}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteBlock(block.id)}>
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
            </div>
          )}

          <div className="space-y-2">
            {getAppointmentsForDate(selectedDate).length === 0 ? (
              <p className="text-muted-foreground">No appointments scheduled</p>
            ) : (
              getAppointmentsForDate(selectedDate).map(apt => (
                <div
                  key={apt.id}
                  onClick={() => setAppointmentDialog({ open: true, appointment: apt })}
                  className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary"
                >
                  <div className="flex items-center gap-3">
                    <div className="text-center">
                      <p className="font-medium">{format(parseISO(apt.start_time), 'HH:mm')}</p>
                      <p className="text-xs text-muted-foreground">{format(parseISO(apt.end_time), 'HH:mm')}</p>
                    </div>
                    <div>
                      <p className="font-medium">{apt.client_name}</p>
                      <p className="text-sm text-muted-foreground">{apt.service_name}</p>
                    </div>
                  </div>
                  <Badge className={apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {apt.status.replace('_', ' ')}
                  </Badge>
                </div>
              ))
            )}
          </div>
        </motion.div>
      )}

      {/* Appointment Details Dialog */}
      <Dialog open={appointmentDialog.open} onOpenChange={(open) => setAppointmentDialog({ open, appointment: appointmentDialog.appointment })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Appointment Details</DialogTitle>
          </DialogHeader>
          {appointmentDialog.appointment && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Client</p>
                  <p className="font-medium">{appointmentDialog.appointment.client_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{appointmentDialog.appointment.client_email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Service</p>
                  <p className="font-medium">{appointmentDialog.appointment.service_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <Badge className={appointmentDialog.appointment.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                    {appointmentDialog.appointment.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium">{format(parseISO(appointmentDialog.appointment.start_time), 'EEEE, d MMMM yyyy')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Time</p>
                  <p className="font-medium">
                    {format(parseISO(appointmentDialog.appointment.start_time), 'HH:mm')} - {format(parseISO(appointmentDialog.appointment.end_time), 'HH:mm')}
                  </p>
                </div>
              </div>
              {appointmentDialog.appointment.notes && (
                <div>
                  <p className="text-sm text-muted-foreground">Notes</p>
                  <p>{appointmentDialog.appointment.notes}</p>
                </div>
              )}
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setRescheduleDialog({ open: true, appointment: appointmentDialog.appointment });
                  }}
                  data-testid="reschedule-btn"
                >
                  Reschedule
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <RescheduleDialog
        open={rescheduleDialog.open}
        appointment={rescheduleDialog.appointment}
        onClose={() => setRescheduleDialog({ open: false, appointment: null })}
        onReschedule={handleReschedule}
      />

      {/* Block Time Dialog */}
      <BlockTimeDialog
        open={blockDialog.open}
        selectedDate={selectedDate}
        onClose={() => setBlockDialog({ open: false })}
        onBlock={handleBlockTime}
      />

      {/* Working Hours Dialog */}
      <WorkingHoursDialog
        open={settingsDialog.open}
        workingHours={workingHours}
        onClose={() => setSettingsDialog({ open: false })}
        onSave={handleSaveWorkingHours}
      />

      {/* Book Appointment Dialog */}
      <BookAppointmentDialog
        open={bookDialog.open}
        selectedDate={selectedDate}
        services={services}
        clients={clients}
        onClose={() => setBookDialog({ open: false })}
        onBook={handleBookAppointment}
      />
    </div>
  );
};

// Reschedule Dialog Component
const RescheduleDialog = ({ open, appointment, onClose, onReschedule }) => {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  useEffect(() => {
    if (appointment) {
      setNewDate(format(parseISO(appointment.start_time), 'yyyy-MM-dd'));
      setNewTime(format(parseISO(appointment.start_time), 'HH:mm'));
    }
  }, [appointment]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (appointment && newDate && newTime) {
      onReschedule(appointment.id, newDate, newTime);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reschedule Appointment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="new-date">New Date</Label>
            <Input
              id="new-date"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={format(new Date(), 'yyyy-MM-dd')}
              required
              data-testid="reschedule-date-input"
            />
          </div>
          <div>
            <Label htmlFor="new-time">New Time</Label>
            <Input
              id="new-time"
              type="time"
              value={newTime}
              onChange={(e) => setNewTime(e.target.value)}
              required
              data-testid="reschedule-time-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="confirm-reschedule-btn">Confirm Reschedule</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Block Time Dialog Component
const BlockTimeDialog = ({ open, selectedDate, onClose, onBlock }) => {
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('18:00');
  const [reason, setReason] = useState('');
  const [blockFullDay, setBlockFullDay] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      setStartDate(dateStr);
      setEndDate(dateStr);
    } else {
      const today = format(new Date(), 'yyyy-MM-dd');
      setStartDate(today);
      setEndDate(today);
    }
  }, [selectedDate, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const start = blockFullDay ? `${startDate}T00:00:00` : `${startDate}T${startTime}:00`;
    const end = blockFullDay ? `${endDate}T23:59:59` : `${endDate}T${endTime}:00`;
    onBlock({
      start_datetime: start,
      end_datetime: end,
      reason: reason || null
    });
    setReason('');
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Block Time Off</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-2">
            <Switch
              id="block-full-day"
              checked={blockFullDay}
              onCheckedChange={setBlockFullDay}
            />
            <Label htmlFor="block-full-day">Block full day(s)</Label>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                data-testid="block-start-date"
              />
            </div>
            {!blockFullDay && (
              <div>
                <Label htmlFor="start-time">Start Time</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                  data-testid="block-start-time"
                />
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                data-testid="block-end-date"
              />
            </div>
            {!blockFullDay && (
              <div>
                <Label htmlFor="end-time">End Time</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  required
                  data-testid="block-end-time"
                />
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="reason">Reason (optional)</Label>
            <Input
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Holiday, Personal time"
              data-testid="block-reason"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="confirm-block-btn">Block Time</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Working Hours Dialog Component
const WorkingHoursDialog = ({ open, workingHours, onClose, onSave }) => {
  const [hours, setHours] = useState([]);

  useEffect(() => {
    if (workingHours && workingHours.length > 0) {
      setHours([...workingHours]);
    } else {
      // Default working hours
      setHours(DAY_NAMES.map((_, i) => ({
        day_of_week: i,
        start_time: '09:00',
        end_time: '18:00',
        is_available: i < 5
      })));
    }
  }, [workingHours, open]);

  const updateHours = (dayIndex, field, value) => {
    const updated = [...hours];
    const dayHours = updated.find(h => h.day_of_week === dayIndex);
    if (dayHours) {
      dayHours[field] = value;
      setHours(updated);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(hours);
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Set Working Hours</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {DAY_NAMES.map((day, index) => {
            const dayHours = hours.find(h => h.day_of_week === index) || {};
            return (
              <div key={day} className="flex items-center gap-4 p-3 bg-secondary/30 rounded-lg">
                <div className="w-24">
                  <span className="font-medium">{day}</span>
                </div>
                <Switch
                  checked={dayHours.is_available}
                  onCheckedChange={(checked) => updateHours(index, 'is_available', checked)}
                  data-testid={`day-toggle-${index}`}
                />
                {dayHours.is_available && (
                  <>
                    <Input
                      type="time"
                      value={dayHours.start_time || '09:00'}
                      onChange={(e) => updateHours(index, 'start_time', e.target.value)}
                      className="w-28"
                      data-testid={`day-start-${index}`}
                    />
                    <span>to</span>
                    <Input
                      type="time"
                      value={dayHours.end_time || '18:00'}
                      onChange={(e) => updateHours(index, 'end_time', e.target.value)}
                      className="w-28"
                      data-testid={`day-end-${index}`}
                    />
                  </>
                )}
                {!dayHours.is_available && (
                  <span className="text-muted-foreground">Not available</span>
                )}
              </div>
            );
          })}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" data-testid="save-working-hours-btn">Save Working Hours</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Book Appointment Dialog Component
const BookAppointmentDialog = ({ open, selectedDate, services, clients, onClose, onBook }) => {
  const [clientType, setClientType] = useState('existing'); // existing or new
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('confirmed');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  
  // New client fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (selectedDate) {
      setBookingDate(format(selectedDate, 'yyyy-MM-dd'));
    } else {
      setBookingDate(format(new Date(), 'yyyy-MM-dd'));
    }
  }, [selectedDate, open]);

  const resetForm = () => {
    setClientType('existing');
    setSelectedClient('');
    setSelectedService('');
    setBookingTime('09:00');
    setNotes('');
    setStatus('confirmed');
    setPaymentStatus('pending');
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    const bookingData = {
      service_id: selectedService,
      date: bookingDate,
      time: bookingTime,
      notes: notes || null,
      status,
      payment_status: paymentStatus
    };

    if (clientType === 'existing') {
      if (!selectedClient) {
        toast.error('Please select a client');
        return;
      }
      bookingData.client_id = selectedClient;
    } else {
      if (!firstName || !lastName || !email) {
        toast.error('Please fill in all client details');
        return;
      }
      bookingData.client_first_name = firstName;
      bookingData.client_last_name = lastName;
      bookingData.client_email = email;
      bookingData.client_phone = phone || null;
    }

    onBook(bookingData);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Generate time slots (hourly from 8am to 8pm)
  const timeSlots = [];
  for (let hour = 8; hour <= 20; hour++) {
    const time = `${hour.toString().padStart(2, '0')}:00`;
    timeSlots.push(time);
  }

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Book New Appointment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client Selection */}
          <div className="space-y-3">
            <Label>Client Type</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="clientType"
                  value="existing"
                  checked={clientType === 'existing'}
                  onChange={() => setClientType('existing')}
                  className="w-4 h-4"
                />
                <span>Existing Client</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="clientType"
                  value="new"
                  checked={clientType === 'new'}
                  onChange={() => setClientType('new')}
                  className="w-4 h-4"
                />
                <span>New Client</span>
              </label>
            </div>
          </div>

          {clientType === 'existing' ? (
            <div>
              <Label htmlFor="client-select">Select Client</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger data-testid="client-select">
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.first_name} {client.last_name} ({client.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-3 p-3 bg-secondary/30 rounded-lg">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="first-name">First Name *</Label>
                  <Input
                    id="first-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    data-testid="new-client-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="last-name">Last Name *</Label>
                  <Input
                    id="last-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                    data-testid="new-client-last-name"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="client-email">Email *</Label>
                <Input
                  id="client-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="john@example.com"
                  required
                  data-testid="new-client-email"
                />
              </div>
              <div>
                <Label htmlFor="client-phone">Phone (optional)</Label>
                <Input
                  id="client-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+44 7123 456789"
                  data-testid="new-client-phone"
                />
              </div>
            </div>
          )}

          {/* Service Selection */}
          <div>
            <Label htmlFor="service-select">Service *</Label>
            <Select value={selectedService} onValueChange={setSelectedService}>
              <SelectTrigger data-testid="service-select">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {services.map(service => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name} ({service.duration_minutes} min - £{service.price})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="booking-date">Date *</Label>
              <Input
                id="booking-date"
                type="date"
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                min={format(new Date(), 'yyyy-MM-dd')}
                required
                data-testid="booking-date"
              />
            </div>
            <div>
              <Label htmlFor="booking-time">Time *</Label>
              <Select value={bookingTime} onValueChange={setBookingTime}>
                <SelectTrigger data-testid="booking-time">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeSlots.map(time => (
                    <SelectItem key={time} value={time}>{time}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="status">Appointment Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="appointment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending_payment">Pending Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="payment-status">Payment Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger data-testid="payment-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="booking-notes">Notes (optional)</Label>
            <Textarea
              id="booking-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={2}
              data-testid="booking-notes"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>Cancel</Button>
            <Button type="submit" data-testid="confirm-booking-btn">Book Appointment</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminCalendar;
